import type { SoundEvent } from './events.js';

/**
 * Sound and vibration (v0.8), made rather than loaded: a few short tones from
 * the Web Audio API, so there is nothing to download, license or cache, and
 * the game stays playable offline exactly as before.
 *
 * Quiet on purpose. A lift game is played on a bus with the volume half up;
 * every sound here is a confirmation of something the player already saw, never
 * the only way to learn it — the screen still says everything.
 *
 * Browsers will not start audio before a gesture, so `unlock` is called from
 * the first tap; until then, and whenever the player has turned sound off,
 * every call is silently nothing.
 */
export class Sound {
  private context: AudioContext | null = null;
  /** Half a second of noise, for the lift's doors: made once, when first wanted. */
  private noise: AudioBuffer | null = null;
  enabled = true;
  /** When the last hurry tick sounded, so a crowd running out ticks once, not ten times. */
  private hurriedAt = Number.NEGATIVE_INFINITY;

  /** The audio context, once a gesture has made one: the music plays on it too (ADR-0056). */
  get audio(): AudioContext | null {
    return this.context;
  }

  /** Call from a user gesture, wanted when either sounds or music are on. Safe to call every time. */
  unlock(wanted = this.enabled): void {
    if (!wanted) return;
    try {
      if (!this.context) {
        const Ctor =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctor) return;
        this.context = new Ctor();
      }
      if (this.context.state === 'suspended') void this.context.resume();
    } catch {
      // No audio on this device: the game is complete without it.
    }
  }

  play(event: SoundEvent): void {
    switch (event) {
      case 'doors':
        // The arrival chime: two soft bell notes, up a fifth.
        this.tone(880, 0.18, 'sine', 0.05);
        this.tone(1320, 0.26, 'sine', 0.04, 0.09);
        return;
      case 'delivered':
        this.tone(660, 0.09, 'triangle', 0.05);
        this.tone(990, 0.08, 'triangle', 0.035, 0.05);
        this.buzz(8);
        return;
      case 'lost':
        this.tone(180, 0.22, 'sine', 0.08, 0, 110);
        this.buzz(30);
        return;
      case 'siren':
        // A crew has arrived (ADR-0029): two short two-tone calls, not a wail.
        for (const [i, f] of [740, 587, 740, 587].entries()) {
          this.tone(f, 0.2, 'triangle', 0.045, i * 0.22);
        }
        this.buzz(60);
        return;
      case 'power':
        // A power-up earned or spent (ADR-0055): three notes up.
        for (const [i, f] of [659, 784, 1047].entries()) {
          this.tone(f, 0.14, 'triangle', 0.045, i * 0.07);
        }
        this.buzz(12);
        return;
      case 'hurry': {
        // Someone's last ten seconds: two quick high ticks, at most every 1.2 s.
        const now = this.context?.currentTime ?? 0;
        if (now - this.hurriedAt < 1.2) return;
        this.hurriedAt = now;
        this.tone(1568, 0.05, 'triangle', 0.05);
        this.tone(1568, 0.05, 'triangle', 0.05, 0.13);
        this.buzz(20);
        return;
      }
    }
  }

  /**
   * The lift's doors on the ride into a shift and back (ADR-0057): a slide, and
   * on closing a soft knock where they meet. The arrival chime is `doors`.
   */
  liftDoors(way: 'close' | 'open'): void {
    const context = this.context;
    if (!this.enabled || !context || context.state !== 'running') return;
    try {
      const start = context.currentTime;
      const seconds = way === 'close' ? 0.38 : 0.42;
      const slide = context.createBufferSource();
      slide.buffer = this.noiseOf(context);
      const band = context.createBiquadFilter();
      band.type = 'bandpass';
      band.Q.value = 1.2;
      band.frequency.setValueAtTime(way === 'close' ? 900 : 480, start);
      band.frequency.exponentialRampToValueAtTime(way === 'close' ? 450 : 900, start + seconds);
      const gain = context.createGain();
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.05, start + seconds * 0.35);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + seconds);
      slide.connect(band).connect(gain).connect(context.destination);
      slide.start(start);
      slide.stop(start + seconds + 0.02);
      if (way === 'close') this.tone(140, 0.12, 'sine', 0.06, seconds - 0.03, 70);
    } catch {
      // Same rule as every sound: missing, never broken.
    }
  }

  private noiseOf(context: AudioContext): AudioBuffer {
    if (this.noise) return this.noise;
    const buffer = context.createBuffer(1, Math.floor(context.sampleRate / 2), context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
    this.noise = buffer;
    return buffer;
  }

  /** The count into a shift: a soft C for 3, 2 and 1, and C with E an octave up for go. */
  count(step: number): void {
    if (step > 0) {
      this.tone(523.25, 0.12, 'triangle', 0.045);
      return;
    }
    this.tone(1046.5, 0.2, 'triangle', 0.05);
    this.tone(1318.5, 0.26, 'sine', 0.03, 0.03);
    this.buzz(12);
  }

  /** A star landing on the results screen: each one a step higher than the last. */
  star(index: number): void {
    const base = [784, 988, 1175][index] ?? 1175;
    this.tone(base, 0.22, 'triangle', 0.05);
    this.tone(base * 2, 0.3, 'sine', 0.025, 0.04);
    this.buzz(15);
  }

  private tone(
    frequency: number,
    seconds: number,
    type: OscillatorType,
    volume: number,
    delay = 0,
    slideTo?: number,
  ): void {
    const context = this.context;
    if (!this.enabled || !context || context.state !== 'running') return;
    try {
      const start = context.currentTime + delay;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, start);
      if (slideTo) oscillator.frequency.exponentialRampToValueAtTime(slideTo, start + seconds);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(volume, start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + seconds);
      oscillator.connect(gain).connect(context.destination);
      oscillator.start(start);
      oscillator.stop(start + seconds + 0.02);
    } catch {
      // A sound that fails to play is a missing sound, never a broken game.
    }
  }

  /** A short vibration, where the phone supports it (Android does; iOS ignores it). */
  private buzz(ms: number): void {
    if (!this.enabled) return;
    try {
      navigator.vibrate?.(ms);
    } catch {
      // Same rule as sound.
    }
  }
}
